// Доступ до прямого ефіру.
//
// Одна відповідь бекенда (/api/live-setting/access) містить і налаштування
// зі Strapi (тумблери), і вердикт по поточному юзеру. Рішення приймає
// бекенд, а не сторінка: playback ID приходить лише тим, кому можна
// дивитися, тож обмеження неможливо обійти з девтулзів.

// Як часто перепитуємо бекенд у відкритій вкладці.
//
// Ефір вмикають, вимикають і перейменовують прямо під час трансляції, а
// /live живе годинами без перезавантаження — без опитування глядач бачив би
// стан на момент відкриття вкладки, поки не натисне F5. У фоновій вкладці
// не питаємо взагалі: відповідь персональна (Cache-Control: no-store), тож
// кожен тик — це справжній запит до Strapi від кожного глядача
const POLL_INTERVAL = 30000

const EMPTY = {
  // Ефір вимкнено тумблером в адмінці: джерел бекенд не віддає взагалі,
  // сайт малює «офлайн» і ховає картку з каталогу
  offline: false,
  mode: null,
  // Яка підписка відкриває ефір у поточному режимі — бекенд уже розклав
  // для нас селект «Доступ» зі Strapi, щоб фронт не знав його значень
  needsTwitch: false,
  needsMono: false,
  restricted: false,
  allowed: false,
  authenticated: false,
  hasTwitchSub: false,
  hasMono: false,
  twitchLogin: null,
  // Ефір із власного стрім-сервера замість Mux — плеєр грає testUrl як є.
  // Ключі лишились із часів, коли це був тестовий режим
  testMode: false,
  testUrl: null,
  streamId: null,
  // Назва ефіру для шапки над відео
  title: null,
  // Який чат поруч із плеєром: 'twitch' або 'live' (власний на
  // вебсокетах); chatPageId — кімната власного чату, chatTwitch — чий канал
  // на Twitch
  chatSource: 'twitch',
  chatPageId: 'live',
  chatTwitch: 'olsior'
}

// Локальна розробка: перевірку доступу пропускаємо.
//
// API у dev дивиться на бойовий Strapi, а куки з нього на localhost не
// їдуть — тому вердикт там завжди «немає підписки», і сторінку /live
// неможливо було відкрити локально взагалі.
//
// Дві умови разом: import.meta.dev (у зібраному сайті це константа false,
// тож увесь блок вирізається бандлером і в прод не потрапляє в принципі)
// і сам хост — щоб `nuxt dev`, відкритий по локальній мережі з телефона
// чи виставлений назовні, лишався під звичайним замком.
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '::1']

const isLocalDev = () =>
  import.meta.dev &&
  import.meta.client &&
  LOCAL_HOSTS.includes(window.location.hostname)

export const useLiveAccess = () => {
  const client = useStrapiClient()

  // server: false — перевірку робимо ТІЛЬКИ в браузері.
  //
  // Інакше вердикт рахувався б двома різними шляхами: на F5 його вирішував
  // сервер (і результат запікався в payload, тобто помилка ще й залипала),
  // а при переході з сайту — клієнт. Будь-яка проблема з кукою на серверному
  // рендері чи проксі перед ним перетворювалась на «у вас немає підписки»
  // рівно після перезавантаження сторінки. Тепер шлях один — той, у якого
  // куки точно є.
  const { data, pending, error, refresh } = useAsyncData(
    'live-access',
    () => client('/live-setting/access'),
    { server: false }
  )

  if (import.meta.client) {
    // Фонове оновлення робимо власним запитом, а не refresh(): той на час
    // запиту піднімає pending і обнуляє data при будь-якому збої. Тобто
    // /live кожні пів хвилини блимала б завантаженням замість плеєра
    // (див. blocked у pages/live.vue), а одна невдала спроба посеред ефіру
    // гасила б його зовсім. Тут попередній стан лишається, поки не приїде
    // новий: сам ефір від нашого невдалого запиту не зник
    const sync = async () => {
      if (document.visibilityState === 'hidden') return

      try {
        data.value = await client('/live-setting/access')
        // Перша перевірка могла й не дійти — тоді сторінка показує «не
        // вдалося перевірити доступ». Вдалий тик має цей екран прибрати,
        // не чекаючи, поки глядач натисне «спробувати ще раз»
        error.value = undefined
      } catch (e) {
        // Мовчки: доступ і назва — не той функціонал, заради якого варто
        // чіпати вже показаний глядачеві екран
      }
    }

    let timer = null

    const startPolling = () => {
      if (!timer) timer = setInterval(sync, POLL_INTERVAL)
    }

    const stopPolling = () => {
      if (!timer) return
      clearInterval(timer)
      timer = null
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopPolling()
        return
      }

      // Повернулись на вкладку — не чекаємо наступного тика: саме тут стан
      // найчастіше й застарілий, бо у фоні ми не питали
      sync()
      startPolling()
    }

    onMounted(() => {
      startPolling()
      document.addEventListener('visibilitychange', handleVisibility)
    })

    onUnmounted(() => {
      stopPolling()
      document.removeEventListener('visibilitychange', handleVisibility)
    })
  }

  const access = computed(() => {
    const value = data.value || EMPTY
    // Локально знімаємо і замок, і тумблер «ефір вимкнено»: відповідь
    // бекенда (якщо вона взагалі є) лишаємо як є — з неї ще можуть прийти
    // streamId чи testUrl, — а міняємо тільки вердикт
    if (!isLocalDev()) return value
    return {
      ...value,
      offline: false,
      restricted: false,
      allowed: true,
      authenticated: true
    }
  })

  // Свідомо fail-closed: якщо бекенд не відповів, ми не знаємо, чи
  // ввімкнені обмеження, тож показуємо помилку з кнопкою «спробувати ще
  // раз», а не відкриваємо ефір усім. Локально навпаки: бойовий Strapi
  // може бути й недоступний, а сторінка все одно має відкриватись
  const failed = computed(() => !isLocalDev() && !!error.value)

  return { access, pending, failed, refresh }
}
